import React from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { COMPANY } from "@/lib/compliance";

/**
 * Terms of Service — full 26-section compliance-reviewed terms.
 * URL stays at /terms. Anywhere the site links to /terms, the label
 * should read "Terms of Service".
 */
export default function TermsPage() {
    return (
        <PageShell
            testId="page-terms"
            kicker="Compliance"
            title="Terms of Service"
            seoDescription="Website terms of use for endurancecasualty.com — Endurance Casualty Insurance Agency. Read before using this site or submitting an inquiry."
        >
            <p className="text-[11px] uppercase tracking-[0.35em] text-brand-goldSoft">
                Website Terms of Use
            </p>
            <p className="text-sm text-brand-muted">
                <strong className="text-brand-ivory">Effective Date:</strong>{" "}
                June 2026
                <br />
                <strong className="text-brand-ivory">Last Updated:</strong>{" "}
                June 2026
            </p>

            <h2>1. Introduction and Acceptance of Terms</h2>
            <p>
                These Terms of Service and Website Terms of Use govern your
                access to and use of{" "}
                <a
                    href={COMPANY.website}
                    rel="noopener noreferrer"
                >
                    {COMPANY.website}
                </a>{" "}
                and any related pages, forms, document-upload links,
                communications, and online services that link to these Terms.
                The Site is operated by {COMPANY.fullLegalName} (&ldquo;
                Endurance Casualty,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo;
                or &ldquo;our&rdquo;). By accessing or using the Site,
                submitting an inquiry, requesting information, sending
                documents, or otherwise communicating with us through the
                Site, you agree to these Terms. If you do not agree, you
                should not use the Site.
            </p>

            <h2>2. Agency Status and Jurisdictional Limitations</h2>
            <p>
                {COMPANY.shortName} is an independent insurance agency and is
                not an insurance company. Insurance products, if available,
                are offered through licensed insurance producers and
                authorized insurers. We transact insurance only in
                jurisdictions where {COMPANY.shortName}, its producers, and
                any applicable insurance partners are properly licensed,
                appointed, or otherwise authorized to do so. Products,
                services, coverage options, appointments, carrier
                availability, underwriting requirements, premiums, deductibles,
                exclusions, endorsements, policy forms, eligibility, and
                service capabilities vary by state, carrier, line of
                authority, and policy terms. Information on the Site should
                not be interpreted as an offer to sell, solicit, negotiate, or
                bind insurance in any jurisdiction where doing so would be
                unlawful. Users may review current licensing information on
                the <Link to="/licensing">Licensing &amp; Disclosures</Link>{" "}
                page and may verify licensing through NIPR, NAIC, or
                applicable state Departments of Insurance.
            </p>

            <h2>3. No Coverage Bound Through the Site</h2>
            <p>
                The Site is provided for general informational and inquiry
                purposes. Submitting an inquiry, requesting a quote, uploading
                documents, sending an email, leaving a message, completing a
                form, or otherwise communicating through the Site does not
                create an insurance policy, bind coverage, modify coverage,
                guarantee eligibility, guarantee premium savings, or obligate
                any insurer, producer, intermediary, or agency to accept or
                insure any risk. Coverage is not effective unless and until it
                is expressly confirmed in writing by an authorized insurer or
                properly licensed and authorized insurance producer. Users
                should not cancel, replace, or change existing insurance
                coverage unless they have received written confirmation that
                replacement or new coverage is in force.
            </p>

            <h2>4. No Guarantee of Quotes, Availability, Savings, or Results</h2>
            <p>
                Any references to quotes, premiums, coverage examples,
                savings, eligibility, risk management, insurance strategies,
                or policy options are illustrative and informational only.
                They are not guarantees. Actual availability, pricing, terms,
                and coverage depend on underwriting, state law, carrier
                guidelines, application information, policy language, payment,
                inspections, claims history, loss control requirements, and
                other factors.
            </p>

            <h2>5. General Information; No Legal, Tax, Financial, or Claims Advice</h2>
            <p>
                The Site provides general insurance information and is not
                intended to provide legal, tax, financial, accounting,
                investment, claims-handling, risk-management, or professional
                advice. Users should consult appropriate professionals
                regarding their specific circumstances. {COMPANY.shortName}{" "}
                does not warrant that any information on the Site is complete,
                current, or applicable to any particular situation.
            </p>

            <h2>6. Eligibility to Use the Site</h2>
            <p>
                Users must be at least 18 years old and legally able to enter
                into these Terms to use the inquiry form or submit information
                through the Site. Users may use the Site only for lawful
                purposes and only in connection with a legitimate insurance
                inquiry, customer-service request, or related communication.
            </p>

            <h2>7. User Responsibilities When Submitting Information</h2>
            <p>
                Users agree that all information submitted through the Site is
                accurate, current, complete, and not misleading. Users are
                responsible for promptly updating information that changes.{" "}
                {COMPANY.shortName} may be unable to provide accurate guidance,
                quotes, submissions, or service if information is incomplete,
                inaccurate, or delayed. Users may not submit information on
                behalf of another person or entity unless authorized to do so.
                Users may not submit false, fraudulent, defamatory, unlawful,
                infringing, malicious, or misleading content.
            </p>

            <h2>8. Sensitive Information and Prohibited Submissions</h2>
            <p>
                For user protection, users should not submit Social Security
                numbers, payment-card numbers, bank-account information,
                medical records, claim documents, identity documents,
                passwords, or other highly sensitive information through the
                general inquiry form, unencrypted email, or any channel that{" "}
                {COMPANY.shortName} has not specifically designated for that
                type of information. If sensitive information is needed,{" "}
                {COMPANY.shortName} will provide instructions for an
                appropriate channel. {COMPANY.shortName} reserves the right to
                delete, disregard, or restrict processing of unsolicited
                sensitive information submitted through the wrong channel.
            </p>

            <h2>9. Document Uploads and Third-Party Document Services</h2>
            <p>
                The Site may include a secure document-upload link provided
                through Dropbox or another third-party document-storage
                provider. Uploaded files are intended to be delivered to an{" "}
                {COMPANY.shortName} intake folder and are not intended to be
                publicly accessible or viewable by other users. By uploading
                documents, users acknowledge that Dropbox or any successor
                provider is a third-party service and may process information
                under its own terms, privacy notice, security practices, and
                retention settings. {COMPANY.shortName} is not responsible for
                third-party service interruptions, account controls, platform
                policies, or actions outside its reasonable control.{" "}
                {COMPANY.shortName} may access, review, retain, use, disclose,
                or delete uploaded documents as reasonably necessary to
                respond to a request, evaluate coverage options, prepare
                submissions, communicate with carriers or insurance
                intermediaries, comply with law, maintain records, or protect
                rights. Users should not upload sensitive information unless{" "}
                {COMPANY.shortName} specifically requests it through an
                approved secure channel.
            </p>

            <h2>10. Privacy Notice Incorporated</h2>
            <p>
                The <Link to="/privacy">Privacy Notice</Link> explains what
                personal information {COMPANY.shortName} collects, how it is
                used, how it is shared, how long it is retained, and what
                rights or choices may apply. The Privacy Notice is
                incorporated into these Terms by reference. If there is a
                conflict between these Terms and the Privacy Notice regarding
                the handling of personal information, the Privacy Notice
                controls to the extent of the conflict.
            </p>

            <h2>11. Sharing with Insurance and Service Providers</h2>
            <p>
                To respond to inquiries, evaluate coverage options, prepare
                submissions, service requests, or comply with law,{" "}
                {COMPANY.shortName} may share information and documents with
                recipients reasonably necessary for those purposes. Such
                recipients may include licensed producers, insurers, carrier
                representatives, managing general agents, managing general
                underwriters, wholesalers, surplus-lines brokers, third-party
                administrators, benefits administrators, claims or
                policy-service providers, customer relationship management
                providers, email and communications vendors, website hosts,
                analytics providers, document-storage providers, compliance
                vendors, professional advisors, regulators, law-enforcement
                authorities, and other service providers acting on{" "}
                {COMPANY.shortName}&apos;s behalf.
            </p>

            <h2>12. Electronic Communications Consent</h2>
            <p>
                When users submit an inquiry, contact {COMPANY.shortName} by
                email, upload documents, or otherwise communicate through the
                Site, they consent to receive communications, disclosures,
                notices, agreements, authorizations, records, and other
                information from {COMPANY.shortName} and its licensed
                producers electronically, including by email, telephone,
                website message, or other electronic means, where permitted by
                law. To access and retain electronic communications, users
                need a current internet browser, a working email address, and
                the ability to save or print electronic records. Users are
                responsible for keeping email addresses and contact
                information current. Users may withdraw electronic
                communications consent or request paper copies by contacting{" "}
                {COMPANY.shortName} at{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> or{" "}
                <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a>.
                Withdrawal of consent does not affect the legal validity of
                communications or records previously provided electronically.
            </p>

            <h2>13. Marketing Communications and Opt-Out Rights</h2>
            <p>
                If a user requests information, submits an inquiry, or
                otherwise communicates with {COMPANY.shortName},{" "}
                {COMPANY.shortName} may contact the user regarding the inquiry
                and related insurance products or services. Where permitted by
                law, {COMPANY.shortName} may also send marketing
                communications about insurance products or services that may
                be of interest. Users may opt out of marketing emails at any
                time by following unsubscribe instructions in the email or by
                contacting{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                Opting out of marketing communications will not prevent{" "}
                {COMPANY.shortName} from sending transactional, servicing,
                legal, compliance, or policy-related communications. If{" "}
                {COMPANY.shortName} uses text messaging, autodialed calls,
                prerecorded calls, or similar communications requiring
                separate consent, {COMPANY.shortName} will obtain any consent
                required by applicable law. Consent to marketing
                communications is not a condition of purchasing insurance
                unless a narrower rule applies and is disclosed at the time of
                consent.
            </p>

            <h2>14. Cookies, Analytics, and Website Security Tools</h2>
            <p>
                The Site may use cookies, analytics, session diagnostics,
                hosting, security, bot-mitigation, content-delivery, and
                similar technologies to operate the Site, protect against
                abuse, understand Site usage, and improve functionality.
                Certain technologies may be provided by third parties, such as
                Cloudflare, PostHog, hosting providers, or similar vendors.
                User browsers may allow disabling cookies or sending privacy
                signals such as Global Privacy Control or Do Not Track. Some
                features may not function properly if cookies or essential
                technologies are disabled.
            </p>

            <h2>15. Third-Party Links, Services, and Content</h2>
            <p>
                The Site may link to or integrate third-party websites,
                platforms, tools, document-upload services, videos, analytics
                providers, carrier resources, regulatory resources, or other
                services. {COMPANY.shortName} does not control and is not
                responsible for the content, privacy practices, terms,
                security, availability, or accuracy of third-party services.
                Use of third-party services is governed by their own terms and
                policies.
            </p>

            <h2>16. Intellectual Property</h2>
            <p>
                The Site, including text, graphics, layouts, logos, images,
                videos, forms, design elements, software, and other content,
                is owned by {COMPANY.shortName} or its licensors and is
                protected by intellectual-property laws. Users may view and
                print reasonable portions of the Site for personal,
                non-commercial use. Users may not copy, modify, distribute,
                sell, scrape, frame, publicly display, create derivative works
                from, or exploit any portion of the Site without prior written
                permission.
            </p>

            <h2>17. Permitted and Prohibited Uses</h2>
            <p>
                Users agree not to use the Site in a way that violates law,
                infringes rights, interferes with Site operation,
                misrepresents identity, attempts unauthorized access, probes
                security controls, introduces malware, scrapes or harvests
                data, overloads infrastructure, submits fraudulent
                information, or uses the Site for unlawful, abusive, or
                competitive purposes.
            </p>

            <h2>18. Accessibility</h2>
            <p>
                {COMPANY.shortName} intends to provide accessible website
                content and insurance information. If users have difficulty
                using the Site, need assistance, or require information in an
                alternate format, they may contact{" "}
                <a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phone}</a> or{" "}
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. See
                our <Link to="/accessibility">Accessibility Statement</Link>{" "}
                for more information.
            </p>

            <h2>19. Security Disclaimer</h2>
            <p>
                {COMPANY.shortName} uses commercially reasonable
                administrative, technical, and physical safeguards designed
                to protect information submitted through the Site. However,
                no method of internet transmission, email transmission, file
                upload, hosting, or electronic storage is completely secure.{" "}
                {COMPANY.shortName} cannot guarantee absolute security,
                uninterrupted access, or error-free operation.
            </p>

            <h2>20. Website Availability and Changes</h2>
            <p>
                {COMPANY.shortName} may modify, suspend, discontinue, or
                restrict access to all or part of the Site at any time without
                notice. {COMPANY.shortName} may update content, forms,
                products, disclosures, availability, or these Terms from time
                to time. Information may become outdated, incomplete, or
                unavailable.
            </p>

            <h2>21. Disclaimer of Warranties</h2>
            <p>
                The Site and all Site content are provided on an &ldquo;as
                is&rdquo; and &ldquo;as available&rdquo; basis. To the fullest
                extent permitted by law, {COMPANY.shortName} disclaims all
                warranties, express or implied, including warranties of
                merchantability, fitness for a particular purpose, title,
                accuracy, availability, security, and non-infringement.{" "}
                {COMPANY.shortName} does not warrant that the Site will be
                uninterrupted, secure, current, complete, or error-free.
            </p>

            <h2>22. Limitation of Liability</h2>
            <p>
                To the fullest extent permitted by law, {COMPANY.shortName}{" "}
                and its owners, officers, directors, employees, producers,
                agents, contractors, affiliates, service providers, and
                licensors will not be liable for indirect, incidental,
                consequential, special, exemplary, punitive, or enhanced
                damages arising from or related to use of, or inability to
                use, the Site, even if advised of the possibility of such
                damages. Nothing in these Terms is intended to limit rights or
                obligations that cannot lawfully be limited, including
                obligations under applicable insurance laws or regulations.
            </p>

            <h2>23. Indemnification</h2>
            <p>
                Users agree to indemnify and hold harmless {COMPANY.shortName}{" "}
                and its owners, officers, directors, employees, producers,
                agents, contractors, affiliates, service providers, and
                licensors from and against claims, losses, liabilities,
                damages, penalties, costs, and expenses, including reasonable
                attorneys&apos; fees, arising out of or relating to misuse of
                the Site, breach of these Terms, violation of law,
                infringement of rights, or submission of inaccurate,
                unauthorized, or unlawful information.
            </p>

            <h2>24. Governing Law and Venue</h2>
            <p>
                These Terms are governed by the laws of the State of New York,
                without regard to conflict-of-law principles, except to the
                extent another jurisdiction&apos;s law applies as a matter of
                non-waivable insurance, privacy, consumer-protection, or other
                law. Any dispute arising out of or relating to these Terms or
                the Site shall be brought in the state or federal courts
                located in Suffolk County, New York, unless prohibited by
                applicable law.
            </p>

            <h2>25. Changes to These Terms</h2>
            <p>
                {COMPANY.shortName} may update these Terms from time to time.
                The &ldquo;Last Updated&rdquo; date identifies the most recent
                revision date. Continued use of the Site after revised Terms
                are posted means the user accepts the revised Terms. If
                changes are material, {COMPANY.shortName} may provide
                additional notice where required by law.
            </p>

            <h2>26. Contact Information</h2>
            <table data-testid="terms-contact-table">
                <tbody>
                    <tr>
                        <th>Agency</th>
                        <td>{COMPANY.fullLegalName}</td>
                    </tr>
                    <tr>
                        <th>Mailing Address</th>
                        <td>{COMPANY.address}</td>
                    </tr>
                    <tr>
                        <th>Phone</th>
                        <td>
                            <a href={`tel:${COMPANY.phoneTel}`}>
                                {COMPANY.phone}
                            </a>
                        </td>
                    </tr>
                    <tr>
                        <th>Email</th>
                        <td>
                            <a href={`mailto:${COMPANY.email}`}>
                                {COMPANY.email}
                            </a>
                        </td>
                    </tr>
                    <tr>
                        <th>Website</th>
                        <td>
                            <a
                                href={COMPANY.website}
                                rel="noopener noreferrer"
                            >
                                {COMPANY.website}
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </PageShell>
    );
}
